import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  setActiveCustomerDisplay,
  setEnabledCustomerDisplay,
} from 'redux/actions/customerDisplayActions'
import { Formatter } from 'Utils/Formatter'
import { ROUTES } from 'Utils/constants'
import { isCustomerDisplayRouteSupported } from 'Views/CustomerDisplay/utils/customerDisplayRouteSupport'
import {
  navigateCustomerDisplayWindow,
  openCustomerDisplayWindow,
  watchSecondaryScreenDisconnect,
} from 'Views/CustomerDisplay/utils/customerDisplayWindow'

const getCartTotals = cart => {
  let totalTax = 0
  let totalPrice = 0

  cart.forEach(item => {
    const price = Number(item?.Price) || 0
    const quantity = Number(item?.Quantity) || 0
    const additionalDiscount = Number(item?.VisitDiscountAmount) || 0
    const taxRate = Number(item?.TaxCategoryValue) || 0
    const subtotalLine = Number(
      Formatter.Money(price * quantity - additionalDiscount, {
        withFormat: false,
        rounded: true,
        decimalPrecision: 2,
      })
    )

    totalTax += subtotalLine * taxRate
    totalPrice += subtotalLine
  })

  const subtotal = Number(
    Formatter.Money(totalPrice, {
      decimalPrecision: 2,
      withFormat: false,
    })
  )
  const itbms = Number(
    Formatter.Money(totalTax, {
      withFormat: false,
    })
  )

  return {
    subtotal,
    itbms,
    total: Number(subtotal) + Number(totalTax),
  }
}

const CUSTOMER_DISPLAY_HEARTBEAT_INTERVAL_MS = 2000
const CUSTOMER_DISPLAY_HEARTBEAT_ACK_TIMEOUT_MS = 6000
const CUSTOMER_DISPLAY_HEARTBEAT_ACK_CHECK_INTERVAL_MS = 1000
const CUSTOMER_DISPLAY_WINDOW_CLOSE_GRACE_PERIOD_MS = 5000

export function useCustomerDisplayController() {
  const dispatch = useDispatch()
  const location = useLocation()
  const [loaderFullScreen, setLoaderFullScreen] = useState(false)
  const [
    isCustomerDisplayWindowConnected,
    setIsCustomerDisplayWindowConnected,
  ] = useState(false)
  const channelRef = useRef(null)
  const preparedCustomerDisplayWindowRef = useRef(null)
  const activeCustomerDisplayWindowRef = useRef(null)
  const customerDisplayCloseTimeoutRef = useRef(null)
  const lastCustomerDisplayHeartbeatAckRef = useRef(null)
  const customerDisplayHeartbeatMonitorStartedAtRef = useRef(null)
  const locationPathnameRef = useRef(location.pathname)

  const { isActiveCustomerDisplay = false, isCustomerDisplayEnabled } =
    useSelector(state => state.customerDisplay) || {}
  const customerDisplayEnabled =
    isCustomerDisplayEnabled ?? isActiveCustomerDisplay
  const shouldMonitorCustomerDisplayConnection =
    customerDisplayEnabled &&
    (isActiveCustomerDisplay || isCustomerDisplayWindowConnected)
  const customerDisplayEnabledRef = useRef(customerDisplayEnabled)
  const isActiveCustomerDisplayRef = useRef(isActiveCustomerDisplay)
  const { CompanyId, BranchOfficeId } =
    useSelector(state => state.company) || {}
  const cart = useSelector(state => state.cart?.cart || [])
  const quoteHeader = useSelector(state => state.cotizacion?.QuoteHeader || {})

  const companyStateRef = useRef({ CompanyId, BranchOfficeId })
  const customerDisplayStateRef = useRef({ cart, quoteHeader })

  useEffect(() => {
    companyStateRef.current = { CompanyId, BranchOfficeId }
  }, [CompanyId, BranchOfficeId])

  useEffect(() => {
    customerDisplayStateRef.current = { cart, quoteHeader }
  }, [cart, quoteHeader])

  useEffect(() => {
    customerDisplayEnabledRef.current = customerDisplayEnabled
  }, [customerDisplayEnabled])

  useEffect(() => {
    isActiveCustomerDisplayRef.current = isActiveCustomerDisplay
  }, [isActiveCustomerDisplay])

  useEffect(() => {
    locationPathnameRef.current = location.pathname
  }, [location.pathname])

  const getChannel = useCallback(() => {
    if (!channelRef.current && typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel('customer_display')
    }

    return channelRef.current
  }, [])

  const clearCustomerDisplayCloseTimeout = useCallback(() => {
    if (!customerDisplayCloseTimeoutRef.current) return

    clearTimeout(customerDisplayCloseTimeoutRef.current)
    customerDisplayCloseTimeoutRef.current = null
  }, [])

  const markCustomerDisplayAsClosed = useCallback(() => {
    clearCustomerDisplayCloseTimeout()
    preparedCustomerDisplayWindowRef.current = null
    activeCustomerDisplayWindowRef.current = null
    lastCustomerDisplayHeartbeatAckRef.current = null
    customerDisplayHeartbeatMonitorStartedAtRef.current = null
    customerDisplayEnabledRef.current = false
    isActiveCustomerDisplayRef.current = false
    setIsCustomerDisplayWindowConnected(false)
    setLoaderFullScreen(false)
    dispatch(setEnabledCustomerDisplay(false))
    dispatch(setActiveCustomerDisplay(false))
  }, [clearCustomerDisplayCloseTimeout, dispatch])

  const closeCustomerDisplayWindow = useCallback(() => {
    const windowsToClose = [
      activeCustomerDisplayWindowRef.current,
      preparedCustomerDisplayWindowRef.current,
    ]

    windowsToClose.forEach(customerDisplayWindow => {
      if (customerDisplayWindow && !customerDisplayWindow.closed) {
        try {
          customerDisplayWindow.close()
        } catch (error) {
          console.warn(
            'No se pudo cerrar la ventana de Customer Display:',
            error
          )
        }
      }
    })

    activeCustomerDisplayWindowRef.current = null
    preparedCustomerDisplayWindowRef.current = null
  }, [])

  const markCustomerDisplayAsDisconnected = useCallback(() => {
    preparedCustomerDisplayWindowRef.current = null
    activeCustomerDisplayWindowRef.current = null
    lastCustomerDisplayHeartbeatAckRef.current = null
    customerDisplayHeartbeatMonitorStartedAtRef.current = null
    isActiveCustomerDisplayRef.current = false
    setIsCustomerDisplayWindowConnected(false)
    setLoaderFullScreen(false)
    dispatch(setActiveCustomerDisplay(false))
  }, [dispatch])

  const sendCompanyData = useCallback(() => {
    getChannel()?.postMessage({
      company: companyStateRef.current.CompanyId,
      branchOffice: companyStateRef.current.BranchOfficeId,
      isChangeCompany: false,
    })
  }, [getChannel])

  const notifyMainRouteSupport = useCallback(() => {
    getChannel()?.postMessage({
      mainRouteIsSupported: isCustomerDisplayRouteSupported(
        locationPathnameRef.current
      ),
    })
  }, [getChannel])

  const sendCustomerDisplayHeartbeat = useCallback(() => {
    getChannel()?.postMessage({
      customerDisplayHeartbeat: Date.now(),
    })
  }, [getChannel])

  const syncCustomerDisplayState = useCallback(() => {
    const pathname = locationPathnameRef.current
    if (!isCustomerDisplayRouteSupported(pathname)) return

    const { cart: currentCart, quoteHeader: currentQuoteHeader } =
      customerDisplayStateRef.current
    const cartTotals = getCartTotals(currentCart)

    getChannel()?.postMessage({
      productCart: currentCart,
      subtotal: cartTotals.subtotal,
      itbms: cartTotals.itbms,
      total: cartTotals.total,
    })

    if (currentQuoteHeader && Object.keys(currentQuoteHeader).length > 0) {
      const shouldShowQuoteDetail =
        pathname.includes(ROUTES.editQuoteHeaderId) &&
        !pathname.includes('/edit_cart')

      getChannel()?.postMessage({
        ...(shouldShowQuoteDetail && { isDetailQuote: true }),
        quoteData: currentQuoteHeader,
        isLoadingDetailQuote: false,
        showDiscount: currentQuoteHeader.ShowDiscountOption,
      })
    }
  }, [getChannel])

  const prepareCustomerDisplayWindow = useCallback(() => {
    preparedCustomerDisplayWindowRef.current = openCustomerDisplayWindow({
      url: 'about:blank',
    })

    return preparedCustomerDisplayWindowRef.current
  }, [])

  const closePreparedCustomerDisplayWindow = useCallback(
    customerDisplayWindow => {
      const windowToClose =
        customerDisplayWindow || preparedCustomerDisplayWindowRef.current

      if (windowToClose && !windowToClose.closed) {
        windowToClose.close()
      }

      if (windowToClose === preparedCustomerDisplayWindowRef.current) {
        preparedCustomerDisplayWindowRef.current = null
      }
    },
    []
  )

  const activateCustomerDisplay = useCallback(
    ({ customerDisplayWindow } = {}) => {
      const preparedWindow =
        customerDisplayWindow || preparedCustomerDisplayWindowRef.current
      const hasPreparedWindow = preparedWindow && !preparedWindow.closed
      const activeWindow = hasPreparedWindow
        ? preparedWindow
        : openCustomerDisplayWindow()
      const opened = hasPreparedWindow
        ? navigateCustomerDisplayWindow(activeWindow)
        : Boolean(activeWindow)

      preparedCustomerDisplayWindowRef.current = null

      if (!opened) return false

      activeCustomerDisplayWindowRef.current = activeWindow
      lastCustomerDisplayHeartbeatAckRef.current = Date.now()
      customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
      customerDisplayEnabledRef.current = true
      isActiveCustomerDisplayRef.current = true
      setIsCustomerDisplayWindowConnected(true)
      clearCustomerDisplayCloseTimeout()
      setLoaderFullScreen(true)
      dispatch(setEnabledCustomerDisplay(true))
      if (isCustomerDisplayRouteSupported(locationPathnameRef.current)) {
        getChannel()?.postMessage({ isSleepMode: false })
      }
      dispatch(setActiveCustomerDisplay(true))
      sendCompanyData()
      notifyMainRouteSupport()

      return true
    },
    [
      clearCustomerDisplayCloseTimeout,
      dispatch,
      getChannel,
      notifyMainRouteSupport,
      sendCompanyData,
    ]
  )

  const deactivateCustomerDisplay = useCallback(() => {
    clearCustomerDisplayCloseTimeout()
    lastCustomerDisplayHeartbeatAckRef.current = null
    customerDisplayHeartbeatMonitorStartedAtRef.current = null
    customerDisplayEnabledRef.current = false
    isActiveCustomerDisplayRef.current = false
    setIsCustomerDisplayWindowConnected(false)
    setLoaderFullScreen(false)
    getChannel()?.postMessage({ windowClose: true })
    closeCustomerDisplayWindow()
    dispatch(setEnabledCustomerDisplay(false))
    dispatch(setActiveCustomerDisplay(false))
  }, [
    clearCustomerDisplayCloseTimeout,
    closeCustomerDisplayWindow,
    dispatch,
    getChannel,
  ])

  const toggleCustomerDisplay = useCallback(() => {
    if (isCustomerDisplayWindowConnected) {
      deactivateCustomerDisplay()
      return
    }

    activateCustomerDisplay()
  }, [
    activateCustomerDisplay,
    deactivateCustomerDisplay,
    isCustomerDisplayWindowConnected,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay) return

    notifyMainRouteSupport()
  }, [location.pathname, isActiveCustomerDisplay, notifyMainRouteSupport])

  useEffect(() => {
    if (!shouldMonitorCustomerDisplayConnection) return undefined

    customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
    sendCustomerDisplayHeartbeat()
    const heartbeatInterval = setInterval(
      sendCustomerDisplayHeartbeat,
      CUSTOMER_DISPLAY_HEARTBEAT_INTERVAL_MS
    )

    return () => {
      clearInterval(heartbeatInterval)
    }
  }, [sendCustomerDisplayHeartbeat, shouldMonitorCustomerDisplayConnection])

  useEffect(() => {
    if (!customerDisplayEnabled) {
      markCustomerDisplayAsDisconnected()
      return undefined
    }

    if (!shouldMonitorCustomerDisplayConnection) return undefined

    if (!customerDisplayHeartbeatMonitorStartedAtRef.current) {
      customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
    }

    const validateCustomerDisplayConnection = () => {
      const lastHeartbeatAckAt = lastCustomerDisplayHeartbeatAckRef.current
      const heartbeatReferenceAt =
        lastHeartbeatAckAt ||
        customerDisplayHeartbeatMonitorStartedAtRef.current

      if (!heartbeatReferenceAt) return

      const heartbeatAge = Date.now() - heartbeatReferenceAt
      if (heartbeatAge > CUSTOMER_DISPLAY_HEARTBEAT_ACK_TIMEOUT_MS) {
        markCustomerDisplayAsDisconnected()
      }
    }

    const heartbeatAckInterval = setInterval(
      validateCustomerDisplayConnection,
      CUSTOMER_DISPLAY_HEARTBEAT_ACK_CHECK_INTERVAL_MS
    )

    return () => {
      clearInterval(heartbeatAckInterval)
    }
  }, [
    customerDisplayEnabled,
    markCustomerDisplayAsDisconnected,
    shouldMonitorCustomerDisplayConnection,
  ])

  useEffect(() => {
    if (!isActiveCustomerDisplay) return

    sendCompanyData()
  }, [CompanyId, BranchOfficeId, isActiveCustomerDisplay, sendCompanyData])

  useEffect(() => {
    if (!isActiveCustomerDisplay) return undefined

    let disposeWatcher
    let isMounted = true

    watchSecondaryScreenDisconnect(() => {
      if (isMounted) {
        deactivateCustomerDisplay()
      }
    }).then(dispose => {
      if (isMounted) {
        disposeWatcher = dispose
        return
      }

      dispose?.()
    })

    return () => {
      isMounted = false
      disposeWatcher?.()
    }
  }, [deactivateCustomerDisplay, isActiveCustomerDisplay])

  useEffect(() => {
    const channel = getChannel()

    if (!channel) return undefined

    const handleMessage = event => {
      const data = event.data || {}

      if (data.customerDisplayHeartbeatAck) {
        if (!customerDisplayEnabledRef.current) return

        lastCustomerDisplayHeartbeatAckRef.current = Date.now()
        customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
        setIsCustomerDisplayWindowConnected(true)

        if (!isActiveCustomerDisplayRef.current) {
          isActiveCustomerDisplayRef.current = true
          dispatch(setActiveCustomerDisplay(true))
        }

        return
      }

      if (data.requestCustomerDisplaySync) {
        syncCustomerDisplayState()
      }

      if (data.customerDisplayWindowClosed) {
        clearCustomerDisplayCloseTimeout()
        customerDisplayCloseTimeoutRef.current = setTimeout(
          markCustomerDisplayAsClosed,
          CUSTOMER_DISPLAY_WINDOW_CLOSE_GRACE_PERIOD_MS
        )
        return
      }

      if (data.customerDisplayReady === true || data.isFullScreen === true) {
        if (!customerDisplayEnabledRef.current) return

        clearCustomerDisplayCloseTimeout()
        lastCustomerDisplayHeartbeatAckRef.current = Date.now()
        customerDisplayHeartbeatMonitorStartedAtRef.current = Date.now()
        setIsCustomerDisplayWindowConnected(true)
        setLoaderFullScreen(false)
        sendCompanyData()
        notifyMainRouteSupport()
      }
    }

    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
    }
  }, [
    getChannel,
    clearCustomerDisplayCloseTimeout,
    markCustomerDisplayAsClosed,
    notifyMainRouteSupport,
    sendCompanyData,
    syncCustomerDisplayState,
    dispatch,
  ])

  useEffect(() => {
    return () => {
      clearCustomerDisplayCloseTimeout()

      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [clearCustomerDisplayCloseTimeout])

  const postToCustomerDisplay = useCallback(
    message => {
      getChannel()?.postMessage(message)
    },
    [getChannel]
  )

  return {
    isActiveCustomerDisplay,
    isCustomerDisplayEnabled: customerDisplayEnabled,
    isCustomerDisplayWindowConnected,
    isCustomerDisplaySwitchActive: isCustomerDisplayWindowConnected,
    loaderFullScreen,
    activateCustomerDisplay,
    closePreparedCustomerDisplayWindow,
    deactivateCustomerDisplay,
    prepareCustomerDisplayWindow,
    toggleCustomerDisplay,
    postToCustomerDisplay,
  }
}
